import React from "react";
import { ValidationError, useForm } from "@formspree/react";
import { tint, transparentize } from "polished";
import styled from "styled-components";
import * as Clrs from "../js/colors";

const pageBg = transparentize(0.68, tint(0.88, Clrs.basegrey));
const cardBg = transparentize(0.28, Clrs.white);

function Contact() {
  const formId = process.env.REACT_APP_FORMSPREE_FORM_ID || "mqejkvqr";
  const [state, handleSubmit] = useForm(formId);

  return (
    <ContactPage>
      <ContactCard>
        <Heading>Contact</Heading>
        <Lead>
          For inquiries about available work, commissions, or exhibitions, send a message below.
        </Lead>

        {state.succeeded ? (
          <SuccessNote role="status">
            Thank you. Your message was sent successfully and I will get back to you soon.
          </SuccessNote>
        ) : null}

        <Form onSubmit={handleSubmit} noValidate>
          <HiddenFieldWrap aria-hidden="true">
            {/* Honeypot field: bots fill this, humans do not. */}
            <input type="text" name="_gotcha" tabIndex="-1" autoComplete="off" />
          </HiddenFieldWrap>

          <input type="hidden" name="_subject" value="Portfolio contact form submission" />

          <Field>
            <FieldLabel htmlFor="contact-name">Name</FieldLabel>
            <TextInput id="contact-name" name="name" type="text" autoComplete="name" required />
          </Field>

          <Field>
            <FieldLabel htmlFor="contact-email">Email</FieldLabel>
            <TextInput
              id="contact-email"
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              required
            />
            <FieldError>
              <ValidationError prefix="Email" field="email" errors={state.errors} />
            </FieldError>
          </Field>

          <Field>
            <FieldLabel htmlFor="contact-message">Message</FieldLabel>
            <TextArea id="contact-message" name="message" rows="7" required />
            <FieldError>
              <ValidationError prefix="Message" field="message" errors={state.errors} />
            </FieldError>
          </Field>

          <FieldError>
            <ValidationError errors={state.errors} />
          </FieldError>

          <SubmitButton type="submit" disabled={state.submitting}>
            Send Message
          </SubmitButton>
        </Form>
      </ContactCard>
    </ContactPage>
  );
}

const ContactPage = styled.section`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 16px 12px 44px;
  box-sizing: border-box;
`;

const ContactCard = styled.div`
  background: ${cardBg};
  border: 1px solid ${transparentize(0.72, Clrs.basegrey)};
  box-shadow: 0 12px 26px rgba(16, 24, 32, 0.14);
  padding: 24px 22px;
`;

const Heading = styled.h2`
  margin: 0;
  color: ${Clrs.basegrey};
  font-size: 30px;
  line-height: 1;
`;

const Lead = styled.p`
  margin: 10px 0 20px;
  color: ${Clrs.grey};
  font-size: 14px;
  line-height: 1.6;
  max-width: 68ch;
`;

const SuccessNote = styled.p`
  margin: 0 0 16px;
  padding: 10px 12px;
  border: 1px solid ${transparentize(0.65, Clrs.success)};
  background: ${transparentize(0.88, Clrs.success)};
  color: ${Clrs.basegrey};
  font-size: 13px;
  line-height: 1.45;
`;

const FieldError = styled.div`
  color: ${Clrs.error};
  font-size: 12px;
  line-height: 1.35;
`;

const Form = styled.form`
  display: grid;
  gap: 14px;
`;

const HiddenFieldWrap = styled.div`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
`;

const FieldLabel = styled.label`
  color: ${Clrs.basegrey};
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.02em;
`;

const sharedInputStyles = `
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${transparentize(0.58, Clrs.basegrey)};
  background: ${pageBg};
  color: ${Clrs.basegrey};
  font-size: 14px;
  font-family: Helvetica, Arial, sans-serif;
  padding: 10px 11px;
  transition: border-color 160ms ease, background 160ms ease;

  &:focus-visible {
    outline: none;
    border-color: ${Clrs.brightgreen};
    background: ${transparentize(0.48, Clrs.white)};
  }
`;

const TextInput = styled.input`
  ${sharedInputStyles}
`;

const TextArea = styled.textarea`
  ${sharedInputStyles}
  resize: vertical;
  min-height: 160px;
`;

const SubmitButton = styled.button`
  justify-self: start;
  border: 1px solid ${transparentize(0.72, Clrs.basegrey)};
  background: ${tint(0.08, Clrs.basegrey)};
  color: ${Clrs.white};
  padding: 10px 14px;
  font-family: Helvetica, Arial, sans-serif;
  font-size: 13px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 160ms ease, filter 160ms ease, opacity 160ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export { Contact };
